import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const useStatisticCacheEntries = ({ statisticType, autoRefresh = true } = {}) => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const activeRequestRef = useRef(0);

  const reload = useCallback(async () => {
    if (!statisticType) return;
    const myRequest = ++activeRequestRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await sendRequest({
        url: '/api/v1/statistic/cache-entries',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params: { statisticType },
      });
      if (activeRequestRef.current !== myRequest) return;
      setEntries(Array.isArray(res?.entries) ? res.entries : []);
    } catch (err) {
      if (activeRequestRef.current !== myRequest) return;
      setError(err?.message || 'Laden der Cache-Einträge fehlgeschlagen.');
    } finally {
      if (activeRequestRef.current === myRequest) setIsLoading(false);
    }
  }, [authCtx, sendRequest, statisticType]);

  useEffect(() => {
    reload();
  }, [statisticType]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const onDone = evt => {
      const detail = evt && evt.detail;
      if (detail && detail.statisticType === statisticType) {
        reload();
      }
    };
    window.addEventListener('statistic-job-done', onDone);
    return () => window.removeEventListener('statistic-job-done', onDone);
  }, [autoRefresh, statisticType, reload]);

  return { entries, isLoading, error, reload };
};

export default useStatisticCacheEntries;
